import { clearLastVisitedRound } from "@client/lib/lastVisitedRound";
import { clearOfflineCache } from "@client/offline/offlineCache";
import * as Sentry from "@client/telemetry/sentry";
import { trySetItem } from "@client/utils/storageWrite";
import { clearObservedAggregateSizes } from "@lib/cacheFirstRounds";

const authTokenKey = "mobileAuthToken";
const cachedUserKey = "mobileCachedUser";

/**
 * A sign-in token that could not be durably stored (quota-exhausted device).
 * By the time this is thrown the magic-link code is already burned
 * server-side, so the caller must tell the user the real cause — a fresh
 * link fails the same way until storage is freed.
 */
export class AuthTokenSaveError extends Error {
  constructor(cause: unknown) {
    super("Could not store the sign-in token", { cause });
    this.name = "AuthTokenSaveError";
  }
}

export function loadAuthToken(): string | null {
  return window.localStorage.getItem(authTokenKey);
}

/**
 * Returns true when the bearer CHANGED (an existing, different token was
 * replaced). Callers that navigate after a sign-in must treat that as an
 * identity transition and go to a clean "/" (`window.location.replace("/")`)
 * rather than reloading: on a cold launch the pre-mount boot redirect may
 * already have rewritten the document to the previous user's saved round, and
 * a reload inherits that URL — clearing the storage key can't undo the
 * history rewrite.
 */
export function saveAuthToken(token: string): boolean {
  // If the bearer changes to a DIFFERENT one, a new account signed in without
  // going through sign-out. Drop the offline identity/nav caches so a stale
  // previous user can't be served offline before getCurrentUser refreshes —
  // which would also feed a stale ownerUserId into the offline round load and
  // skip the SPAStorage owner-change wipe. (The IndexedDB caches are
  // owner-scoped/purged once the live identity resolves online; offline, with
  // no cached identity, the app can't open a round under the wrong user.) Only
  // on an actual change — re-saving the same token on relaunch must preserve
  // the offline cache.
  const existing = window.localStorage.getItem(authTokenKey);
  const bearerChanged = existing != null && existing !== token;

  if (bearerChanged) {
    clearOfflineCache();
    clearLastVisitedRound();
    clearObservedAggregateSizes();
    window.localStorage.removeItem(cachedUserKey);
  }

  try {
    window.localStorage.setItem(authTokenKey, token);
  } catch (error) {
    // On a quota-exhausted device this throw used to surface as the generic
    // "link expired" toast — sending the user chasing fresh links that kept
    // failing the same way. Report it attributably and throw typed so the
    // sign-in flow can name the real cause.
    const wrapped = new AuthTokenSaveError(error);
    Sentry.captureException(wrapped);
    throw wrapped;
  }

  return bearerChanged;
}

/**
 * Remove the native session token (and cached user) so sign-out actually
 * signs the user out on native builds. The bundled app authenticates via this
 * bearer token rather than a cookie, so clearing the Auth.js cookie alone
 * would leave the session intact. Harmless no-op on web.
 */
export function clearAuthToken(): void {
  window.localStorage.removeItem(authTokenKey);
  window.localStorage.removeItem(cachedUserKey);
  // The offline identity/nav caches are identity-scoped too. In particular
  // the all-platform current-user entry would re-mount the signed-in shell
  // from the stale identity after the post-sign-out reload, until
  // getCurrentUser settles UNAUTHORIZED (sign-out must purge every cache —
  // see offline/AGENTS.md).
  clearOfflineCache();
  // The saved last-visited round is also identity-scoped nav state: sign-out
  // ends in a full document load at "/", and the iPad boot redirect would
  // bounce the next (possibly different) user into the previous user's round.
  clearLastVisitedRound();
  // Also cleared here (not just on the sign-out page): the native account
  // menu calls this directly, and afterwards there is no prior bearer for
  // saveAuthToken's change-detection to fire on.
  clearObservedAggregateSizes();
}

export function loadCachedUser<T>(): T | null {
  const raw = window.localStorage.getItem(cachedUserKey);

  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw);

    if (parsed == null || typeof parsed !== "object") {
      return null;
    }

    return parsed as T;
  } catch {
    return null;
  }
}

export function saveCachedUser<T>(user: T): void {
  // Best-effort: this runs inside a React effect with no error boundary
  // above it, so a quota throw here unmounted the whole app on every launch.
  // A stale cached copy (or none) just means offline boot falls back.
  trySetItem(window.localStorage, cachedUserKey, JSON.stringify(user));
}
